import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { useEntitlements } from '../../context/EntitlementsContext';
import { routeGateFor } from '../../lib/planGates';
import { plansCopy } from '../../copy/plans';
import { EmptyState } from '@/components/ui/empty-state';
import { BlockedPage } from '../BlockedPage';
import { canManageBilling, currentPlanSentence, lockedSentence } from './upgradeText';

// What a locked page shows when someone reaches it by URL (a bookmark, an
// old link, a shared path): one calm card that says which plan includes it,
// never a blank page or an error. Nav already hides these pages.

interface LockedFeatureCardProps {
  feature: string;
  plural?: boolean;
  requiredPlan?: string;
  /** A one-row banner for a locked panel inside a page, rather than the page-sized card. */
  compact?: boolean;
  className?: string;
}

export function LockedFeatureCard({ feature, plural, requiredPlan, compact, className }: LockedFeatureCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, refresh } = useEntitlements();
  const isAdmin = canManageBilling(user);
  // The plan may have changed since it was read (an upgrade in another tab or
  // on another server): check once when the lock shows.
  useEffect(() => { void refresh(); }, [refresh]);
  const body = [currentPlanSentence(plan), isAdmin ? null : plansCopy.askAdmin].filter(Boolean).join(' ');

  return (
    <EmptyState
      icon={Lock}
      title={lockedSentence(feature, plural, requiredPlan)}
      body={body || undefined}
      action={isAdmin ? { label: plansCopy.seePlans, onClick: () => navigate('/organisation/billing') } : undefined}
      variant={compact ? 'banner' : 'card'}
      className={className ?? (compact ? undefined : 'mx-auto mt-6 max-w-xl')}
    />
  );
}

/**
 * Renders the page, or the upgrade card when the plan is known not to
 * include it. Unknown (still loading, or the endpoint failed) renders the
 * page: the server still refuses anything the plan doesn't cover.
 */
export function RouteEntitlementGuard({ pathname, children }: { pathname: string; children: React.ReactNode }) {
  const { known, requiredPlan } = useEntitlements();
  const gate = routeGateFor(pathname);
  if (gate && known(gate.entitlement) === false) {
    return (
      <BlockedPage pathname={pathname}>
        <LockedFeatureCard
          feature={gate.feature}
          plural={gate.plural}
          requiredPlan={requiredPlan(gate.entitlement)}
          className="mx-auto max-w-xl"
        />
      </BlockedPage>
    );
  }
  return <>{children}</>;
}
